/**
 * useMatrixPresence - Matrix-specific wrapper for the generic usePresence hook
 * 
 * This is a convenience wrapper that provides the familiar API for matrix presence
 * tracking while using the generic usePresence hook under the hood.
 * 
 * For new features, consider using usePresence directly with your own resourceType.
 * 
 * USAGE:
 *   const { viewers, isConnected } = useMatrixPresence(projectId, discipline, enabled);
 */

import usePresence from './usePresence';

/**
 * Hook to track presence in a matrix view
 * 
 * @param {string} projectId - The project ID
 * @param {string} discipline - The discipline (e.g., 'piping', 'hvac')
 * @param {boolean} enabled - Whether presence is enabled (from discipline config)
 * @param {Function} [onDataChange] - Callback for live data change events from other users
 * @returns {Object} { viewers, isConnected, connectionState, error, socketId }
 */
const useMatrixPresence = (projectId, discipline, enabled = true, onDataChange = null) => {
  // Combine projectId and discipline into a single resourceId
  // Format: "projectId:discipline" (e.g., "123:piping")
  const resourceId = projectId && discipline ? `${projectId}:${discipline}` : null;
  
  return usePresence('matrix', resourceId, enabled && !!resourceId, onDataChange);
};

export default useMatrixPresence;
